import { useMemo } from 'react'
import {
  ArrayFieldModel,
  CheckboxFieldModel,
  FieldType,
  FormField,
  FormModelValidationConfigModel,
  FormModelValidationMode,
  LocalFile,
  SectionFieldModel,
} from '@types'
import {
  createBaseFieldFrom,
  generateNewIdsForField,
  isFieldWithOptions,
} from '../components/FormBuilder/FormBuilder.helper'

interface UseFormBuilderProps {
  fields: FormField[]
  validationConfig?: FormModelValidationConfigModel
  setFields: React.Dispatch<React.SetStateAction<FormField[]>>
  setLocalFiles: React.Dispatch<React.SetStateAction<Record<string, LocalFile[]>>>
  setValidationConfig: React.Dispatch<React.SetStateAction<FormModelValidationConfigModel | undefined>>
}

interface FormBuilderHook {
  availableSections: SectionFieldModel[]
  filteredFields: FormField[]
  fieldsToRemoveInMenu: string[]
  handleLocalFilesChange: (fieldId: string, files: LocalFile[]) => void
  handleFieldAdd: (type: FieldType | 'validate') => void
  handleFieldArrayAdd: (fieldId: string, type: FieldType) => void
  handleFieldArrayRemove: (arrayFieldId: string, fieldId: string) => void
  handleDeleteOptionInArray: (arrayFieldId: string, fieldId: string, optionId: string) => void
  handleDeleteOption: (fieldId: string, optionId: string) => void
  handleFieldDelete: (fieldId: string) => void
  handleFieldMove: (fieldId: string, direction: 'up' | 'down') => void
  handleFieldDuplicate: (fieldId: string) => void
  handleFieldChange: (fieldId: string, updates: Partial<FormField>) => void
  handleValidationConfigChange: (allowAdminEdit: boolean) => void
  handleValidationConfigDelete: () => void
}

export const useFormBuilder = ({
  fields,
  validationConfig,
  setFields,
  setLocalFiles,
  setValidationConfig,
}: UseFormBuilderProps): FormBuilderHook => {
  const availableSections = useMemo(
    () =>
      (fields.filter(({ type }) => type === FieldType.Section) as SectionFieldModel[]).filter(
        ({ label }) => label !== '',
      ) || [],
    [fields],
  )

  const handleLocalFilesChange = (fieldId: string, files: LocalFile[]) => {
    setLocalFiles((prev) => ({
      ...prev,
      [fieldId]: files,
    }))
  }

  const handleFieldAdd = (type: FieldType | 'validate') => {
    if (type === 'validate') {
      setValidationConfig({
        mode: FormModelValidationMode.Submit,
        allowAdminEdit: true,
      })
      return
    }

    const newField = createBaseFieldFrom(type)

    setFields((prev) => {
      if (type === FieldType.HeaderPicture) {
        return [newField, ...prev.filter((field) => field.type !== FieldType.HeaderPicture)]
      }

      if (type === FieldType.Title) {
        const hasHeaderPicture = prev.some((field) => field.type === FieldType.HeaderPicture)
        if (!hasHeaderPicture) return [newField, ...prev]

        return prev.reduce((acc, field) => {
          acc.push(field)
          if (field.type === FieldType.HeaderPicture) {
            acc.push(newField)
          }
          return acc
        }, [] as FormField[])
      }

      return [...prev, newField]
    })
  }

  const handleFieldArrayAdd = (fieldId: string, type: FieldType) => {
    const newField = createBaseFieldFrom(type)

    setFields((prev) => {
      const fieldIndex = prev.findIndex((field) => field.id === fieldId)
      if (fieldIndex === -1) return prev

      const field = prev[fieldIndex] as ArrayFieldModel
      const col = field.cols.length > 0 ? field.cols[0] : { id: fieldId, fields: [] }

      col.fields = [...col.fields, newField]
      field.cols = [col]

      const newFields = [...prev]
      newFields[fieldIndex] = field

      return newFields
    })
  }

  const handleFieldArrayRemove = (arrayFieldId: string, fieldId: string) => {
    setFields((prev) => {
      const fieldIndex = prev.findIndex((field) => field.id === arrayFieldId)
      if (fieldIndex === -1) return prev

      const field = prev[fieldIndex] as ArrayFieldModel
      const col = field.cols[0]

      col.fields = col.fields.filter(({ id }) => id !== fieldId)
      field.cols = [col]

      const newFields = [...prev]
      newFields[fieldIndex] = field
      return newFields
    })
  }

  const handleDeleteOptionInArray = (arrayFieldId: string, fieldId: string, optionId: string) => {
    setFields((prev) => {
      const arrayFieldIndex = prev.findIndex((field) => field.id === arrayFieldId)
      if (arrayFieldIndex === -1) return prev

      const newFields = [...prev]
      const arrayField = { ...newFields[arrayFieldIndex] } as ArrayFieldModel

      const col = arrayField.cols[0]
      const newCol = {
        ...col,
        fields: col.fields.map((field) => {
          if (!isFieldWithOptions(field.type) || !('options' in field) || field.id !== fieldId) {
            return field
          }
          return {
            ...field,
            options: field.options.filter((option) => option.id !== optionId),
          }
        }),
      }

      arrayField.cols = [newCol]
      newFields[arrayFieldIndex] = arrayField

      return newFields
    })
  }

  const handleDeleteOption = (fieldId: string, optionId: string) => {
    setFields((prev) => {
      const fieldIndex = prev.findIndex((field) => field.id === fieldId)
      if (fieldIndex === -1) return prev

      const field = prev[fieldIndex] as CheckboxFieldModel
      const newOptions = field.options.filter((option) => option.id !== optionId)
      const newField = { ...field, options: newOptions }

      const newFields = [...prev]
      newFields[fieldIndex] = newField
      return newFields
    })
  }

  const handleFieldDelete = (fieldId: string) => {
    setFields((prev) => prev.filter((field) => field.id !== fieldId))
  }

  const handleFieldMove = (fieldId: string, direction: 'up' | 'down') => {
    setFields((prev) => {
      const fieldIndex = prev.findIndex((field) => field.id === fieldId)
      if (fieldIndex === -1) return prev

      const newFields = [...prev]
      const field = newFields[fieldIndex]

      if (direction === 'up' && fieldIndex > 0) {
        newFields[fieldIndex] = newFields[fieldIndex - 1]
        newFields[fieldIndex - 1] = field
      } else if (direction === 'down' && fieldIndex < newFields.length - 1) {
        newFields[fieldIndex] = newFields[fieldIndex + 1]
        newFields[fieldIndex + 1] = field
      }

      return newFields
    })
  }

  const handleFieldDuplicate = (fieldId: string) => {
    setFields((prev) => {
      const fieldToDuplicate = prev.find((field) => field.id === fieldId)
      if (!fieldToDuplicate || !('label' in fieldToDuplicate)) return prev

      const duplicatedField = generateNewIdsForField(fieldToDuplicate)

      if ('label' in duplicatedField) {
        duplicatedField.label = `${fieldToDuplicate.label} - copie`
      }

      const fieldIndex = prev.findIndex((field) => field.id === fieldId)
      const newFields = [...prev]
      newFields.splice(fieldIndex + 1, 0, duplicatedField)
      return newFields
    })
  }

  const handleFieldChange = (fieldId: string, updates: Partial<FormField>) => {
    const newFields = fields.map((field) =>
      field.id === fieldId
        ? {
            ...field,
            ...updates,
          }
        : field,
    )
    setFields(newFields as FormField[])
  }

  const handleValidationConfigChange = (allowAdminEdit: boolean) => {
    setValidationConfig({
      mode: FormModelValidationMode.Submit,
      allowAdminEdit,
    })
  }

  const handleValidationConfigDelete = () => {
    setValidationConfig({
      mode: FormModelValidationMode.OnChange,
      allowAdminEdit: true,
    })
  }

  const filteredFields = useMemo(() => {
    return [
      ...fields.filter((field) => field.type === FieldType.HeaderPicture),
      ...fields.filter((field) => field.type === FieldType.Title),
      ...fields.filter((field) => field.type !== FieldType.Title && field.type !== FieldType.HeaderPicture),
    ]
  }, [fields])

  const fieldsToRemoveInMenu = [
    {
      type: 'title',
      shouldRemove: fields.filter(({ type }) => type === FieldType.Title).length === 1,
    },
    {
      type: 'header_picture',
      shouldRemove: fields.filter(({ type }) => type === FieldType.HeaderPicture).length === 1,
    },
    {
      type: 'validate',
      shouldRemove: validationConfig?.mode === FormModelValidationMode.Submit,
    },
  ]
    .filter(({ shouldRemove }) => shouldRemove)
    .map(({ type }) => type)

  return {
    availableSections,
    filteredFields,
    fieldsToRemoveInMenu,
    handleLocalFilesChange,
    handleFieldAdd,
    handleFieldArrayAdd,
    handleFieldArrayRemove,
    handleDeleteOptionInArray,
    handleDeleteOption,
    handleFieldDelete,
    handleFieldMove,
    handleFieldDuplicate,
    handleFieldChange,
    handleValidationConfigChange,
    handleValidationConfigDelete,
  }
}
